import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Iconify from "src/components/iconify";
import PasswordTextField from "src/components/form/fields/PasswordTextField";
import PasswordCheckTextField from "src/components/form/fields/PasswordCheckTextField";
import { useChangeUsersPWMutation } from "src/features/users/usersApiSlice";
import {
  getPasswordPolicyChecks,
  getFailedPasswordPolicyMessages,
  PASSWORD_POLICY_RULES,
  PASSWORD_POLICY_MESSAGE,
} from "src/utils/passwordPolicy";

PasswordChangeDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  user: PropTypes.object,
};

export default function PasswordChangeDialog({ handleClose, user }) {
  const [changeUsersPW, { isLoading }] = useChangeUsersPWMutation();
  const [submitFeedback, setSubmitFeedback] = useState(null);

  const { handleSubmit, reset, watch, control } = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const newPassword = watch("password") || "";

  // Live password policy checklist
  const criteriaState = useMemo(() => {
    const checks = getPasswordPolicyChecks(newPassword);
    return PASSWORD_POLICY_RULES.map((criterion) => ({
      ...criterion,
      fulfilled: checks[criterion.key],
    }));
  }, [newPassword]);

  const handleCheckMatch = (checkPwd) => {
    if (watch("password") !== checkPwd) {
      return "Die Passwörter stimmen nicht überein";
    }
    return true;
  };

  const onSubmit = async (data) => {
    setSubmitFeedback(null);

    const failedCriteria = getFailedPasswordPolicyMessages(data.password);
    if (failedCriteria.length > 0) {
      setSubmitFeedback({ type: "error", message: PASSWORD_POLICY_MESSAGE });
      return;
    }

    try {
      await changeUsersPW({
        userId: user?._id,
        newPassword: data.password,
      }).unwrap();
      setSubmitFeedback({
        type: "success",
        message: "Das Passwort wurde erfolgreich aktualisiert.",
      });
      reset();
    } catch (error) {
      setSubmitFeedback({
        type: "error",
        message:
          error?.data?.message || "Das Passwort konnte nicht geändert werden.",
      });
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={handleClose}>
      <DialogTitle sx={{ pb: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h5">Passwort ändern</Typography>
            <Typography variant="body2" color="text.secondary">
              Neues Passwort für{" "}
              <strong>
                {user?.firstName} {user?.lastName}
              </strong>{" "}
              {user?.mail ? `(${user.mail})` : ""}
            </Typography>
          </Box>
          <IconButton aria-label="Schließen" onClick={handleClose}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2}>
          {/* Live password policy checklist */}
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              bgcolor: "background.default",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Passwort-Anforderungen
            </Typography>
            <Stack spacing={0.75}>
              {criteriaState.map((criterion) => (
                <Box
                  key={criterion.key}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Iconify
                    icon={
                      criterion.fulfilled
                        ? "eva:checkmark-circle-2-fill"
                        : "eva:close-circle-fill"
                    }
                    width={18}
                    sx={{
                      color: criterion.fulfilled ? "success.main" : "text.disabled",
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {criterion.message}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {submitFeedback ? (
            <Alert severity={submitFeedback.type}>{submitFeedback.message}</Alert>
          ) : null}

          <Divider />

          <form onSubmit={handleSubmit(onSubmit)} id="user-password-form">
            <Stack spacing={2}>
              <PasswordTextField
                name="password"
                control={control}
                label="Neues Passwort"
                fullWidth
              />
              <PasswordCheckTextField
                name="confirmPassword"
                label="Passwort bestätigen"
                control={control}
                handleCheckMatch={handleCheckMatch}
                fullWidth
              />
            </Stack>
          </form>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Abbrechen
        </Button>
        <Button
          form="user-password-form"
          type="submit"
          color="secondary"
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? "Speichern..." : "Passwort ändern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
